import { z } from "zod";
import { INFERENCE_GEOGRAPHIES, residencyViewSchema } from "./models.ts";
import { projectIdSchema } from "./projects.ts";
import { entityIdSchema, tenantIdSchema } from "./tenancy.ts";

/**
 * The backoffice residency screen (`GET /api/admin/residency`): per level in the caller's scope,
 * the resolved `models.residency`, its source and lock. Metadata only, like project governance:
 * personal projects are listed to tenant admins only, without their names.
 */
export const residencyReportSchema = z.object({
  /** The geographies the instance grants: without `global`, residency is locked to `eu`. */
  geographies: z.array(z.enum(INFERENCE_GEOGRAPHIES)),
  /** Tenant admins only. `lockedHere`: this level locks the value for every level below. */
  tenant: residencyViewSchema.extend({ lockedHere: z.boolean() }).nullable(),
  entities: z.array(
    residencyViewSchema.extend({
      lockedHere: z.boolean(),
      entityId: entityIdSchema,
      name: z.string(),
      parentId: entityIdSchema.nullable(),
      sourceEntityId: entityIdSchema.nullable(),
    }),
  ),
  projects: z.array(
    residencyViewSchema.extend({
      projectId: projectIdSchema,
      /** Null for a personal project. */
      name: z.string().nullable(),
      /** The owning entity; null for a personal project. */
      ownerEntityId: entityIdSchema.nullable(),
    }),
  ),
  /** Projects listed above that resolve to `global`. */
  globalProjects: z.int().min(0),
});
export type ResidencyReport = z.infer<typeof residencyReportSchema>;

/** The instance ceiling as operators see it (`GET /api/operator/residency`): no tenant content. */
export const operatorResidencySchema = z.object({
  geographies: z.array(z.enum(INFERENCE_GEOGRAPHIES)),
  tenants: z.array(
    z.object({
      tenantId: tenantIdSchema,
      name: z.string(),
      /** Whether any live project of the tenant resolves to `global`. */
      anyProjectGlobal: z.boolean(),
    }),
  ),
});
export type OperatorResidency = z.infer<typeof operatorResidencySchema>;
